export type AppLocale = 'zh-CN' | 'zh-TW'

const STORAGE_KEY = 'language'
const PLATFORM_KEY = 'platform'
const PERSIST_KEY = 'state'

const normalizeLocale = (value: unknown): AppLocale | null => {
  if (typeof value !== 'string') return null
  const normalized = value.replace('_', '-').toLowerCase()
  if (['zh-cn', 'zh-hans', 'zh-sg'].includes(normalized)) return 'zh-CN'
  if (['zh-tw', 'zh-hant', 'zh-hk', 'zh-mo'].includes(normalized)) return 'zh-TW'
  return null
}

const getPersistedLocale = (): AppLocale | null => {
  try {
    const raw = window.localStorage.getItem(PERSIST_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return normalizeLocale(parsed?.user?.language)
  } catch (error) {
    return null
  }
}

const getPathLocale = (pathname: string): AppLocale | null => {
  const lower = pathname.toLowerCase()
  if (lower === '/tw' || lower.startsWith('/tw/')) return 'zh-TW'
  if (lower === '/cn' || lower.startsWith('/cn/')) return 'zh-CN'
  if (lower === '/zh-tw' || lower.startsWith('/zh-tw/')) return 'zh-TW'
  if (lower === '/zh-cn' || lower.startsWith('/zh-cn/')) return 'zh-CN'
  return null
}

const getIpLocale = (): AppLocale | null => {
  const win = window as any
  const locale = normalizeLocale(win.__IP_LOCALE__)
  if (locale) return locale
  if (typeof win.__IP_COUNTRY__ === 'string') {
    const country = win.__IP_COUNTRY__.toUpperCase()
    if (country === 'CN') return 'zh-CN'
    if (country === 'TW') return 'zh-TW'
  }
  return null
}

const getBrowserLocale = (): AppLocale | null => {
  const languages = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language]
  for (const lang of languages) {
    const normalized = normalizeLocale(lang)
    if (normalized) return normalized
  }
  return null
}

export const resolveInitialLocale = (): AppLocale => {
  const pathLocale = getPathLocale(window.location.pathname)
  if (pathLocale) return pathLocale

  const stored = normalizeLocale(window.localStorage.getItem(STORAGE_KEY))
  if (stored) return stored

  const persisted = getPersistedLocale()
  if (persisted) return persisted

  const browserLocale = getBrowserLocale()
  if (browserLocale) return browserLocale

  const ipLocale = getIpLocale()
  if (ipLocale) return ipLocale

  return 'zh-TW'
}

export const persistLocale = (locale: AppLocale): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale)
    window.localStorage.setItem(PLATFORM_KEY, locale)
  } catch (error) {
    // Ignore storage errors to avoid blocking app boot.
  }

  if (document?.documentElement) {
    document.documentElement.lang = locale
  }
}

type StoreLike = { commit: (type: string, payload: { key: string; val: AppLocale }) => void }

export const applyLocaleToStore = (store: StoreLike, locale: AppLocale): void => {
  store.commit('user/__set', { key: 'language', val: locale })
  store.commit('user/__set', { key: 'platform', val: locale })
}
